import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import HeaderImage from "../../assets/img/miPerfil.svg";
import { useStateAuthValue } from "../../context/Auth/AuthState";
import { showToast, ToastType } from "../../utils/ToastUtil";
import { useToast } from "../../context/Toast/ToastProvider";
import { Role } from "../../models/enums";
import PersonalInformationPetForm from "./Forms/pet/PersonalInformationPetForm";
import petFormModel, {
  petFormValidation,
} from "./Forms/pet/petSchemaValidation";
import { Formik, Form } from "formik";
import PetMedicalHistoryForm from "./Forms/pet/PetMedicalHistoryForm";
import { ReviewProfilePet } from "./Forms/pet/ReviewProfile";
import { SkeletonFormLoading } from "./skeleton-form-loading";
import { GetBackgroundData } from "./get-background-data";
import {
  GetRacePetBySpecieId,
  GetSpeciesPet,
  SaveHealthBooklet,
  SaveStudiesMedicalHistory,
  UpdatePetAsync,
} from "../../services/petService";
import { GetPreexistingDisease, GetSurgerys } from "../../services/userService";
import { getSizeName } from "../../utils/FormUtil";
import { PicturePetSection } from "./Forms/pet/picture-pet-section";

const petSteps = [
  "Información de la mascota",
  "Antecedentes Médicos",
  "Foto Mascota",
  "Datos Ingresados",
];

const newPetRegistration = (props) => {
  const petFromEdit = props?.location?.state?.updatePet;
  const [initialValues, setInitialValues] = useState(petFormModel.valuesForm);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(!!petFromEdit);
  const [{ userData, userToken }] = useStateAuthValue();
  const currentRole = userData?.active_role;
  const isPatient = currentRole === Role.PATIENT;
  const toastDispatch = useToast();
  const [activeStep, setActiveStep] = useState(0);
  const [validationSchema, setValidationSchema] = useState({});
  const [formModel, setFormModel] = useState({});
  const [steps, setSteps] = useState([]);
  const currentValidationSchema = validationSchema[activeStep];
  const lastStep = steps.length - 1;
  const isLastStep = activeStep === lastStep;
  const otherRaceOptionUid = "fa678a6b-d349-498b-875d-aad05eb86652";

  const renderStepContent = (step, formikProps) => {
    switch (step) {
      case 0:
        return (
          <PersonalInformationPetForm
            formModel={formModel}
            initialValues={initialValues}
          />
        );
      case 1:
        return (
          <PetMedicalHistoryForm
            formModel={formModel}
            formikProps={formikProps}
          />
        );
      case 2:
        return (
          <PicturePetSection
            formModel={formModel}
            initialValues={initialValues}
          />
        );
      case 3:
        return <ReviewProfilePet formModel={formModel} />;
      default:
        return <div>No encontrado</div>;
    }
  };

  const handleSubmit = async (values, actions) => {
    if (isLastStep) {
      await submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const getSpeciePets = async () => {
    const res = await GetSpeciesPet(userToken);
    const data = res.data;
    return data;
  };

  const getRacePetsBySpecieId = async (specieId) => {
    const res = await GetRacePetBySpecieId(specieId, userToken);
    const data = res.data;
    return data;
  };

  const GetSurgeries = async () => {
    try {
      const res = await GetSurgerys(userToken);
      const data = Array.isArray(res.data) ? res.data : [];
      return data;
    } catch (error) {
      return [];
    }
  };

  const getPreexistingDisease = async () => {
    try {
      const res = await GetPreexistingDisease(userToken);
      const data = Array.isArray(res.data) ? res.data : [];
      return data;
    } catch (error) {
      return [];
    }
  };

  const submitForm = async (values) => {
    const errorMessage = "Ocurrió un problema guardando tu mascota";
    try {
      const successMessage = "¡Mascota guardada exitosamente!";

      const newUserData = {
        id: values.id || null,
        userData: {
          alias: values.first_name,
          firstName: values.first_name,
          sex: values.sex_str,
          birthDate: values.birth_date,
        },
        fullProfile: userData.patient_dto.full_profile,
        patientPreExistingDiseases: values.pre_existing_disease.map((item) => ({
          preExistingDiseaseId: item.id,
        })),
        patientFrequentMedications:
          values.frequent_medication_check_box === "false"
            ? []
            : values.frequent_medication.map((item) => ({
                frequentMedicationId: item.name,
              })),
        patientSurgeries: values.surgeries.map((item) => ({
          surgeryId: item.name,
        })),
        patientVaccines: [],
        size: values.size.value,
        castrated: values.castrated_str === "true" ? true : false,
        race:
          values.race.race === "Otro" ? values.customRace : values.race.race,
        species: values.specie.species,
        allergies:
          values.allergies_check_box === "false" ? "" : values.allergies,
        furColor: values.furColor || "",
        // changePhotoAttempts: 100,
      };

      if (!values.id) {
        delete newUserData.id;
      }
      const formData = new FormData();
      formData.append("puppyPhoto", values.imagePuppy);
      formData.append("adultPhoto", values.imageAdult);
      formData.append("patientDataJson", JSON.stringify(newUserData));

      if (!newUserData.id) {
        showToast(
          toastDispatch,
          "Ha ocurrido un error actualizando la mascota",
          ToastType.ERROR
        );
        return;
      } else {
        await UpdatePetAsync(newUserData.id, formData, userToken);
        await SaveStudiesMedicalHistory(
          newUserData.id,
          values.medicalStudies,
          userToken
        );
        await SaveHealthBooklet(
          newUserData.id,
          values.healthBooklet,
          userToken
        );
      }
      showToast(toastDispatch, successMessage, ToastType.SUCCESS);
      history.push("/mis-mascotas");
      setActiveStep(lastStep);
    } catch {
      showToast(toastDispatch, errorMessage, ToastType.ERROR);
    }
  };

  useEffect(() => {
    if (isPatient) {
      setSteps(petSteps);
      setFormModel(petFormModel);
      setValidationSchema(petFormValidation);
    }
  }, [isPatient]);

  const getInitialValues = async () => {
    if (petFromEdit) {
      const [species, surgeries, preExistingDisease] = await Promise.all([
        getSpeciePets(),
        GetSurgeries(),
        getPreexistingDisease(),
      ]);

      const specieFound = species.find(
        (item) => item.species === petFromEdit.species
      );

      let races = [];
      if (specieFound) {
        [races] = await Promise.all([getRacePetsBySpecieId(specieFound.id)]);
      }

      let raceFound = races.find((item) => item.race === petFromEdit.race);
      let customRace = "";
      if (!raceFound && specieFound) {
        raceFound = {
          id: otherRaceOptionUid,
          race: "Otro",
          speciesId: specieFound.id,
        };
        customRace = petFromEdit.race;
      }

      const surgeriesFound = surgeries
        .filter((item) =>
          petFromEdit.patientSurgeries.some(
            (surgery) => surgery.surgeryId === item.name
          )
        )
        .map((item) => ({ id: item.id, name: item.name }));

      const preExistingDiseaseFound = preExistingDisease
        .filter((item) =>
          petFromEdit.patientPreExistingDiseases.some(
            (disease) => disease.preExistingDiseaseId === item.id
          )
        )
        .map((item) => ({ id: item.id, name: item.name }));

      const timestamp = Date.parse(petFromEdit.userData.birthDate);
      const date = new Date(timestamp);

      if (!petFromEdit?.allergies) {
        petFromEdit.allergies = [];
      }
      if (!petFromEdit?.patientFrequentMedications) {
        petFromEdit.patientFrequentMedications = [];
      }

      return {
        first_name: petFromEdit.userData.firstName || "",
        sex_str: petFromEdit.userData.sex || "",
        castrated_str: petFromEdit.castrated.toString() || "",
        weight: petFromEdit.weight || "",
        height: petFromEdit.height || "",
        id: petFromEdit?.id || null,
        size: {
          value: petFromEdit.size || "",
          name: getSizeName(petFromEdit.size) || "",
        },
        customRace: customRace || "",
        race: raceFound
          ? {
              id: raceFound.id,
              race: raceFound.race,
            }
          : races[0],
        specie: specieFound
          ? {
              id: specieFound.id,
              species: specieFound.species,
            }
          : species[0],
        birth_date: date,
        changePhotoAttempts: petFromEdit.changePhotoAttempts || 0,
        pre_existing_disease: preExistingDiseaseFound || [],
        frequent_medication:
          petFromEdit.patientFrequentMedications.map((item) => ({
            id: item.id,
            value: item.id,
            name: item.frequentMedicationId,
            label: item.frequentMedicationId,
          })) || [],
        surgeries: surgeriesFound || [],
        vaccines: [],
        furColor: petFromEdit.furColor || "",
        allergies: petFromEdit.allergies || "",
        urlPuppy: "",
        urlAdult: "",
        imagePuppy: null,
        imageAdult: null,
        healthBooklet: [],
        medicalStudies: [],
        allergies_check_box:
          petFromEdit.allergies.length > 0 ? "true" : "false",
        frequent_medication_check_box:
          petFromEdit.patientFrequentMedications.length > 0 ? "true" : "false",
      };
    }

    return {
      ...petFormModel.valuesForm,
    };
  };

  useEffect(() => {
    const loadInitialValues = async () => {
      try {
        setIsLoading(true);
        const values = await getInitialValues();
        setInitialValues(values);
      } catch (error) {
        showToast(
          toastDispatch,
          "Ha ocurrido un error obteniendo datos la mascota",
          ToastType.ERROR
        );
        setInitialValues(petFormModel.valuesForm);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialValues();
  }, [petFromEdit]);

  return (
    <div className="full-registration">
      <div className="header">
        <img src={HeaderImage} alt="Registro Completo" />
        <h2>Guardar mascota</h2>
      </div>

      <div className="column">
        {isLoading ? (
          <SkeletonFormLoading />
        ) : (
          <>
            <Stepper alternativeLabel activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label} className="step">
                  <StepLabel className="step">{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {userData?.id_number && steps.length && (
              <div className="container">
                <Formik
                  initialValues={initialValues}
                  validationSchema={currentValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                  validateOnMount
                >
                  {(formikProps) => {
                    const { isSubmitting } = formikProps;
                    return (
                      <Form id="fullRegistrationForm">
                        <GetBackgroundData petFromEdit={petFromEdit} />
                        {renderStepContent(activeStep, formikProps)}
                        <div className="btn-wrapper">
                          {activeStep !== 0 && (
                            <Button
                              disabled={isSubmitting}
                              onClick={handleBack}
                              variant="outlined"
                              className={`btn btn--back ${
                                isSubmitting && "btn--disabled"
                              }`}
                            >
                              Volver
                            </Button>
                          )}
                          <>
                            <Button
                              disabled={isSubmitting}
                              type="submit"
                              variant="contained"
                              color="primary"
                              endIcon={
                                isSubmitting && <CircularProgress size={24} />
                              }
                              className={`btn ${
                                isLastStep ? "btn--save" : "btn--next"
                              } ${isSubmitting && "btn--disabled"}`}
                            >
                              {isLastStep ? "Finalizar" : "Siguiente"}
                            </Button>
                          </>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default newPetRegistration;
